import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from '../services/spinner/spinner.service';
import { ToastService } from '../services/notification/toast.service';
import { AllowedOriginsService } from './allowed-origins.service';
import { AllowedOrigin, AllowedOriginsConfig } from './allowed-origins.model';
import { SecurityService } from '../services/security.service';

@Component({
  selector: 'app-allowed-origins',
  templateUrl: './allowed-origins.component.html',
  styleUrls: ['./allowed-origins.component.scss'],
  standalone: false
})
export class AllowedOriginsComponent implements OnInit {
  config: AllowedOriginsConfig | null = null;
  dataSource = new MatTableDataSource<AllowedOrigin>([]);
  displayedColumns = ['origin', 'created_by', 'created_at', 'actions'];

  isOriginModalOpen = false;
  editingOrigin: AllowedOrigin | null = null; // null = alta
  originForm: FormGroup;

  // Validación de origen: http(s)://host(:puerto) — estilo "origin", sin path/query
  private readonly ORIGIN_PATTERN = /^https?:\/\/[a-zA-Z0-9.-]+(:\d+)?$/;

  constructor(
    private allowedOriginsService: AllowedOriginsService,
    private formBuilder: FormBuilder,
    private spinner: SpinnerService,
    private toast: ToastService,
    private translate: TranslateService,
    private router: Router,
    private securityService: SecurityService
  ) {
    this.originForm = this.formBuilder.group({
      origin: ['', [Validators.required, Validators.pattern(this.ORIGIN_PATTERN)]],
    });
  }

  ngOnInit(): void {
    this.loadConfig();
  }

  get mode(): 'all' | 'restricted' | null {
    return this.config ? this.config.mode : null;
  }

  get canAddMore(): boolean {
    return !!this.config && this.config.total_available_origins > 0;
  }

  private getPartnerId(): number | null {
    const partnerId = this.securityService.getTokenPartner();
    return partnerId ? Number(partnerId) : null;
  }

  loadConfig(): void {
    const partnerId = this.getPartnerId();
    if (!partnerId) {
      return this.toast.showInfo(this.translate.instant('allowedOrigins.messages.missingPartner'));
    }

    this.spinner.open();
    this.allowedOriginsService.list(partnerId).subscribe(
      (response) => {
        this.spinner.close();
        if (response && response.success === false) {
          return this.toast.showError(response.message);
        }
        this.config = response.data;
        this.dataSource = new MatTableDataSource<AllowedOrigin>(
          this.config && this.config.origins ? this.config.origins : []
        );
      },
      (err) => {
        this.spinner.close();
        this.toast.showError(err);
      }
    );
  }

  openCreateModal(): void {
    if (!this.canAddMore) {
      return this.toast.showWarning(this.translate.instant('allowedOrigins.messages.limitReached'));
    }
    this.editingOrigin = null;
    this.originForm.reset({ origin: '' });
    this.isOriginModalOpen = true;
  }

  openEditModal(origin: AllowedOrigin): void {
    this.editingOrigin = origin;
    this.originForm.reset({ origin: origin.origin });
    this.isOriginModalOpen = true;
  }

  closeModal(): void {
    this.isOriginModalOpen = false;
  }

  saveOrigin(): void {
    if (this.originForm.invalid) {
      return this.toast.showWarning(this.translate.instant('allowedOrigins.modal.originInvalid'));
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      return this.toast.showInfo(this.translate.instant('allowedOrigins.messages.missingPartner'));
    }

    const origin = (this.originForm.value.origin || '').trim();
    const isEdit = !!(this.editingOrigin && this.editingOrigin.id != null);

    const request$ = isEdit
      ? this.allowedOriginsService.update(partnerId, this.editingOrigin!.id!, origin)
      : this.allowedOriginsService.create(partnerId, origin);

    this.spinner.open();
    request$.subscribe(
      (response) => {
        this.spinner.close();
        if (response && response.success === false) {
          return this.toast.showError(response.message);
        }
        this.toast.showSuccess(
          this.translate.instant(
            isEdit ? 'allowedOrigins.messages.updated' : 'allowedOrigins.messages.created'
          )
        );
        this.closeModal();
        this.loadConfig();
      },
      (err) => {
        this.spinner.close();
        this.toast.showError(err);
      }
    );
  }

  deleteOrigin(origin: AllowedOrigin): void {
    if (origin.id == null) {
      return;
    }
    if (!confirm(this.translate.instant('allowedOrigins.messages.confirmDelete'))) {
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      return this.toast.showInfo(this.translate.instant('allowedOrigins.messages.missingPartner'));
    }

    this.spinner.open();
    this.allowedOriginsService.remove(partnerId, origin.id).subscribe(
      (response) => {
        this.spinner.close();
        if (response && response.success === false) {
          return this.toast.showError(response.message);
        }
        this.toast.showSuccess(this.translate.instant('allowedOrigins.messages.deleted'));
        this.loadConfig();
      },
      (err) => {
        this.spinner.close();
        this.toast.showError(err);
      }
    );
  }

  switchToAll(): void {
    if (!confirm(this.translate.instant('allowedOrigins.messages.confirmSwitchToAll'))) {
      return;
    }
    const partnerId = this.getPartnerId();
    if (!partnerId) {
      return this.toast.showInfo(this.translate.instant('allowedOrigins.messages.missingPartner'));
    }
    this.runSwitch(
      this.allowedOriginsService.switchToAll(partnerId),
      'allowedOrigins.messages.switchedToAll'
    );
  }

  switchToRestricted(): void {
    const partnerId = this.getPartnerId();
    if (!partnerId) {
      return this.toast.showInfo(this.translate.instant('allowedOrigins.messages.missingPartner'));
    }
    this.runSwitch(
      this.allowedOriginsService.switchToRestricted(partnerId),
      'allowedOrigins.messages.switchedToRestricted'
    );
  }

  private runSwitch(request$: any, successKey: string): void {
    this.spinner.open();
    request$.subscribe(
      (response: any) => {
        this.spinner.close();
        if (response && response.success === false) {
          return this.toast.showError(response.message);
        }
        this.toast.showSuccess(this.translate.instant(successKey));
        this.loadConfig();
      },
      (err: any) => {
        this.spinner.close();
        this.toast.showError(err);
      }
    );
  }

  goBack(): void {
    this.router.navigate(['/my-portals']);
  }
}
